import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import analysisService from '../services/analysisService';

const FinishedInterviewPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [resultId, setResultId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const hasStartedAnalysisRef = useRef(false);

  // Extract data from navigation state
  const { chatGroupId, codeData, sessionContext, sessionTime } =
    location.state || {};

  const analyzeInterview = useCallback(async () => {
    // Prevent multiple calls using ref (race condition safe)
    if (hasStartedAnalysisRef.current || isAnalyzing) {
      return;
    }

    hasStartedAnalysisRef.current = true; // Set immediately to prevent race conditions

    try {
      setIsAnalyzing(true);
      setError(null);

      const result = await analysisService.startInterviewAnalysis(
        chatGroupId,
        codeData,
        sessionContext
      );
      setResultId(result.result_id);
    } catch (error) {
      console.error('Failed to start analysis:', error);
      setError(
        error instanceof Error ? error.message : 'Failed to start analysis'
      );
      hasStartedAnalysisRef.current = false; // Reset flag on error so user can retry
    } finally {
      setIsAnalyzing(false);
    }
  }, [chatGroupId, codeData, sessionContext, isAnalyzing]);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }

    // If we don't have the required data, redirect to practice
    if (!chatGroupId || !codeData || !sessionContext) {
      navigate('/practice');
      return;
    }

    // Start the analysis process
    analyzeInterview();
  }, [user, chatGroupId, codeData, sessionContext, navigate, analyzeInterview]);

  const handleViewResults = () => {
    if (resultId) {
      navigate(`/practice/result/${resultId}`);
    }
  };

  const handleBackToPractice = () => {
    navigate('/practice');
  };

  const handleRetry = () => {
    hasStartedAnalysisRef.current = false;
    setError(null);
    analyzeInterview();
  };

  return (
    <div className='min-h-screen bg-gray-50 flex items-center justify-center px-4'>
      <div className='max-w-md w-full'>
        {/* Congratulations Section */}
        <div className='text-center mb-8'>
          <div className='w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6'>
            <svg
              className='w-10 h-10 text-green-600'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
              />
            </svg>
          </div>

          <h1 className='text-3xl font-bold text-gray-900 mb-3'>
            🎉 Interview Complete!
          </h1>

          <p className='text-lg text-gray-600 mb-4'>
            Congratulations on finishing your coding interview!
          </p>

          {sessionTime && (
            <div className='bg-white rounded-lg p-4 mb-6 shadow-sm'>
              <div className='text-sm text-gray-500 mb-1'>Total Time</div>
              <div className='text-2xl font-bold text-blue-600'>
                {sessionTime}
              </div>
            </div>
          )}

          {/* Usage Error Warning
          {usageError && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <svg
                  className="h-5 w-5 text-yellow-400 mt-0.5 mr-3 flex-shrink-0"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <h3 className="text-sm font-medium text-yellow-800">
                    Usage Limit Notice
                  </h3>
                  <p className="mt-1 text-sm text-yellow-700">
                    {usageError}
                  </p>
                </div>
              </div>
            </div>
          )} */}
        </div>

        {/* Analysis Status */}
        {isAnalyzing && (
          <div className='bg-white rounded-lg shadow-sm p-6 mb-6'>
            <div className='flex items-center justify-center mb-4'>
              <div className='relative w-8 h-8'>
                <div className='absolute inset-0 border-2 border-gray-200 rounded-full'></div>
                <div className='absolute inset-0 border-2 border-transparent border-t-blue-600 rounded-full animate-spin'></div>
              </div>
            </div>
            <p className='text-center text-gray-600 mb-2'>
              Analyzing your interview performance...
            </p>
            <p className='text-center text-sm text-gray-500'>
              This will take 2-3 minutes. Please wait while we generate your
              detailed feedback.
            </p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className='bg-red-50 border border-red-200 rounded-lg p-4 mb-6'>
            <div className='flex items-start'>
              <svg
                className='h-5 w-5 text-red-400 mt-0.5 mr-3 flex-shrink-0'
                viewBox='0 0 20 20'
                fill='currentColor'
              >
                <path
                  fillRule='evenodd'
                  d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z'
                  clipRule='evenodd'
                />
              </svg>
              <div>
                <h3 className='text-sm font-medium text-red-800'>
                  Analysis Failed
                </h3>
                <p className='mt-1 text-sm text-red-700'>{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Success State - Show when we have result_id */}
        {resultId && !isAnalyzing && (
          <div className='bg-green-50 border border-green-200 rounded-lg p-4 mb-6'>
            <div className='flex items-start'>
              <svg
                className='h-5 w-5 text-green-400 mt-0.5 mr-3 flex-shrink-0'
                viewBox='0 0 20 20'
                fill='currentColor'
              >
                <path
                  fillRule='evenodd'
                  d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
                  clipRule='evenodd'
                />
              </svg>
              <div>
                <h3 className='text-sm font-medium text-green-800'>
                  Analysis Started!
                </h3>
                <p className='mt-1 text-sm text-green-700'>
                  Your interview analysis is now running in the background. You
                  can check back in 2-3 minutes for your detailed results.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className='space-y-3'>
          {resultId && !isAnalyzing && (
            <button
              onClick={handleViewResults}
              className='w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors'
            >
              Check Analysis Progress
            </button>
          )}

          {/* Always show back to practice button */}
          <button
            onClick={handleBackToPractice}
            className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
              resultId && !isAnalyzing
                ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {resultId && !isAnalyzing
              ? 'Back to Practice'
              : 'Return to Practice'}
          </button>

          {/* Retry button for errors */}
          {error && (
            <button
              onClick={handleRetry}
              className='w-full bg-orange-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-orange-700 transition-colors'
            >
              Retry Analysis
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FinishedInterviewPage;
